import {
  TFunctionType,
  renderTranslationString
} from 'shared/entities/localization';
import {
  ActionSetVarOperationServer,
  SetVarsKind
} from 'shared/entities/varOperation';

import { IRootStore } from '../../store/rootStore';
import {
  getDateVarOperationActualName,
  getNumberVarOperationActualName
} from '../../vars';
import { getVarActualName } from '../../vars/utils/getVarActualName';
import {
  ActualNameCreator,
  createActualBlockName,
  normalizeCommonFields
} from '../common';

import {
  ActionSetVarNormalizer,
  ActionSetVarVariableType,
  IActionSetVarParams
} from './client';

const createActionSetVarsActualNameBaseFromServer = ({
  variables,
  rootStore,
  t
}: {
  variables: ActionSetVarOperationServer[];
  rootStore: IRootStore;
  t: TFunctionType;
}) => {
  return variables
    .map((variable) => {
      const specialVar = rootStore.variablesStore.getSpecialVariableByKey(
        variable.lhs
      );

      const varKey = specialVar
        ? renderTranslationString(specialVar.title, t)
        : variable.lhs;

      let varValue;

      switch (variable.kind) {
        case SetVarsKind.numOp:
          varValue = getNumberVarOperationActualName(variable.rhs);
          break;
        case SetVarsKind.dateOp:
          varValue = getDateVarOperationActualName(variable.rhs, t);
          break;
        default:
          varValue = getVarActualName(
            { key: variable.lhs, value: variable.rhs },
            rootStore,
            t
          );
      }

      return `${varKey} = ${varValue}`;
    })
    .filter(Boolean)
    .join(
      ` ${t('condition.mode.and', {
        ns: 'entities'
      })} `
    );
};

const createActionSetVarsActualNameBase = ({
  variables,
  rootStore,
  t
}: {
  variables: ActionSetVarVariableType[];
  rootStore: IRootStore;
  t: TFunctionType;
}) => {
  return variables
    .map(({ variable }) => {
      const specialVar = rootStore.variablesStore.getSpecialVariableByKey(
        variable.key
      );

      return `${
        specialVar ? renderTranslationString(specialVar.title, t) : variable.key
      } = ${getVarActualName(variable, rootStore, t)}`;
    })
    .filter(Boolean)
    .join(
      ` ${t('condition.mode.and', {
        ns: 'entities'
      })} `
    );
};

export const createActionSetVarsActualName: ActualNameCreator<
  IActionSetVarParams
> = ({ params, ...rest }) =>
  createActionSetVarsActualNameBase({
    variables: params.variables.items,
    ...rest
  });

export const normalizeActionSetVar: ActionSetVarNormalizer = (raw, stores) => {
  return {
    ...normalizeCommonFields(raw, stores),
    actualName: (t) =>
      createActualBlockName({
        paramsName: createActionSetVarsActualNameBaseFromServer({
          variables: raw.params,
          rootStore: stores.rootStore,
          t
        }),
        blockKind: raw.kind,
        blockName: raw.name,
        t,
        ...stores
      })
  };
};
